/**
 * P-270 · the sticky column header above a Tasks list.
 *
 * Modelled on My Work's column strip, which is the only measured header the product owns
 * (`discovery/ui-reference/my-work/01-anatomy.json`: the strip is 34 px tall at y 258, its cells
 * padded `8px 8px 8px 0`, and only the sortable one carries a control). The numbers are re-used
 * rather than invented, for the same reason round T2 re-used them for the rest of this page.
 *
 * It is **one** header per list, not one per group: the groups below it are buckets of the same
 * six columns, and a strip repeated inside every bucket would say the same thing four times and
 * still scroll away. Sticky at the top of the list, so the words survive a long Overdue bucket.
 *
 * Alignment is by construction: each cell's width is the same `--ez-tasks-*-w` custom property
 * the row's cell reads (`tasks.css`), so the two lines cannot drift. `verify-tasks-local.mjs`
 * asserts the boxes are equal anyway, at 1440 and 1024.
 *
 * On a phone it is not rendered at all — `mobile.css` hides it, and the row's accessible names
 * plus `TaskLegend` carry the same information (the card's phone half).
 */
import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { TaskColumnKey } from './columns';
import { nextTaskSort, taskColumnSortState, taskColumns, taskLegend } from './columns';
import type { TaskSelectState } from './selection';
import type { TaskSort, TaskTab } from './types';

export interface TaskListHeaderProps {
  tab: TaskTab;
  sort: TaskSort;
  onSort(next: TaskSort): void;
  /**
   * **P-305** · whether this header draws the 6 px resize gutters.
   *
   * `false` on a phone, where the header is not drawn at all and the row is a two-line card:
   * `mobile.css` already hides the strip, and this keeps the handles out of the *DOM* as well,
   * so a phone cannot reach one by any route — including the tab key.
   */
  resizable?: boolean;
  /**
   * P-305 · pointer-down on a column's gutter. The header hands over the column and the width
   * that column is **painted** at; the page owns the gesture (`lib/use-column-resize.ts`) and
   * the preview, because the custom properties the widths travel in live above this strip.
   */
  onResizeStart?(event: ReactPointerEvent<HTMLElement>, key: TaskColumnKey, startWidth: number): void;
  /**
   * P-305 · a width committed **without** a drag — the keyboard's arrow keys. It takes the
   * absolute number, not a delta, so the clamp and the write are the drag's, once.
   */
  onResize?(key: TaskColumnKey, width: number): void;
  /** P-305 · double-click on the gutter: this column goes back to the stylesheet's arithmetic. */
  onResetWidth?(key: TaskColumnKey): void;
  /** Which columns currently carry a width of this user's, so the gutter can say so. */
  resizedColumns?: readonly TaskColumnKey[];
  /**
   * P-285 · the header box speaks for **every loaded row**: none of them, some, or all.
   * `'some'` is the dash, which is the only state that tells a person the list is partly in.
   */
  selectState: TaskSelectState;
  /** Ticking selects every loaded row; unticking clears the whole selection. */
  onSelectAll(selected: boolean): void;
}

/**
 * P-285 · the one tri-state checkbox this page draws — the list header's and every group's.
 *
 * `indeterminate` is a **property**, not an attribute: there is no way to express it in JSX, so
 * it is written to the node in an effect. One component rather than two copies of that effect,
 * because the two boxes differ only in their label and their test id, and a second copy is how
 * the group box would have ended up with a checkbox that ticks and never dashes.
 */
export function TaskSelectBox({
  state,
  label,
  testId,
  className = 'ez-tasks__selectall',
  onChange,
}: {
  state: TaskSelectState;
  label: string;
  testId: string;
  className?: string;
  onChange(selected: boolean): void;
}) {
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (box.current !== null) box.current.indeterminate = state === 'some';
  }, [state]);

  return (
    <label className={className} title={label}>
      <input
        ref={box}
        type="checkbox"
        checked={state === 'all'}
        aria-label={label}
        data-state={state}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="ez-tasks__sr">{label}</span>
    </label>
  );
}

/** `↑` / `↓`, the same two glyphs My Work's sort control draws. */
function arrowFor(state: 'ascending' | 'descending' | 'none'): string {
  return state === 'descending' ? '↓' : '↑';
}

/**
 * P-305 · one column's resize gutter — the board grid's, on this page's header.
 *
 * Measured off `table.css`'s `.ez-column-header__resize`: 6 px wide, hung 3 px past the cell's
 * right edge so it sits in the 8 px gap between two columns, `cursor: col-resize`,
 * `touch-action: none` (a touch drag must not scroll the list under the finger).
 *
 * It is a `role="separator"` with an accessible name, like the grid's, and it is **focusable**:
 * the Tasks header cells carry no menu, so there is nowhere to hang the card's "Reset width"
 * item, and a handle that only a mouse can reach would be the one control on this page a
 * keyboard cannot work. Arrow keys move it by 16 px (⇧ by 64), `Home` resets the column — which
 * is what the gutter's double-click does with a pointer.
 */
function ColumnResizeHandle({
  columnKey,
  label,
  resized,
  onResizeStart,
  onResize,
  onResetWidth,
}: {
  columnKey: TaskColumnKey;
  label: string;
  resized: boolean;
  onResizeStart(event: ReactPointerEvent<HTMLElement>, key: TaskColumnKey, startWidth: number): void;
  onResize(key: TaskColumnKey, width: number): void;
  onResetWidth(key: TaskColumnKey): void;
}) {
  /** The **painted** width of the cell this gutter hangs off — the only honest start (P-305). */
  const widthOfColumn = (node: HTMLElement): number => {
    const cell = node.parentElement;
    return cell === null ? 0 : Math.round(cell.getBoundingClientRect().width);
  };

  return (
    <span
      className="ez-tasks__colhead-resize"
      data-testid={`tasks-colhead-resize-${columnKey}`}
      data-resized={resized ? '' : undefined}
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      tabIndex={0}
      onPointerDown={(event) => onResizeStart(event, columnKey, widthOfColumn(event.currentTarget))}
      onDoubleClick={() => onResetWidth(columnKey)}
      onKeyDown={(event) => {
        if (event.key === 'Home') {
          event.preventDefault();
          onResetWidth(columnKey);
          return;
        }
        const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
        if (step === 0) return;
        event.preventDefault();
        /*
         * The same commit a drag makes, as a one-shot: `onResizeStart` is a pointer gesture, so
         * a key press cannot borrow it — it reports the absolute width and the page clamps it.
         */
        onResize(columnKey, widthOfColumn(event.currentTarget) + step * (event.shiftKey ? 64 : 16));
      }}
    />
  );
}

export function TaskListHeader({
  tab,
  sort,
  onSort,
  selectState,
  onSelectAll,
  resizable = false,
  onResizeStart,
  onResize,
  onResetWidth,
  resizedColumns = [],
}: TaskListHeaderProps) {
  const resized = new Set<TaskColumnKey>(resizedColumns);
  return (
    <div
      className="ez-tasks__colhead"
      role="row"
      data-tab={tab}
      data-testid="tasks-colhead"
      aria-label="Task columns"
    >
      {/*
        P-285 · the checkbox column is no longer a blank spacer: it carries **Select all**.
        It is deliberately **not** a `role="columnheader"` — it names no column, it *is* one
        control, and a seventh columnheader would make the strip claim seven columns to a screen
        reader where the row has six names and a box.
      */}
      <span className="ez-tasks__colhead-cell ez-tasks__colhead-select">
        <TaskSelectBox
          state={selectState}
          label="Select all"
          testId="tasks-select-all"
          onChange={onSelectAll}
        />
      </span>

      {taskColumns(tab).map((column) => {
        const state = taskColumnSortState(column, sort);
        const next = nextTaskSort(column, sort);
        return (
          <span
            key={column.key}
            role="columnheader"
            aria-sort={column.sorts ? state : undefined}
            className={`ez-tasks__colhead-cell ez-tasks__colhead-${column.key}`}
            data-sorted={state === 'none' ? undefined : state}
            data-testid={`tasks-colhead-${column.key}`}
          >
            {next === null ? (
              <span className="ez-tasks__colhead-label">{column.label}</span>
            ) : (
              <button
                type="button"
                className="ez-tasks__colhead-sort"
                data-testid={`tasks-colhead-sort-${column.key}`}
                onClick={() => onSort(next)}
              >
                <span className="ez-tasks__colhead-label">{column.label}</span>
                <span className="ez-tasks__colhead-arrow" aria-hidden="true" data-active={state === 'none' ? undefined : ''}>
                  {arrowFor(state)}
                </span>
              </button>
            )}
            {/*
              P-305 · every column gets one, **Item included** — that is the column the report
              was about. The select and action columns do not: neither names a column of the row
              (`columns.ts`), and the actions cell is 0 px wide on two of the three tabs.
            */}
            {resizable && onResizeStart !== undefined && onResize !== undefined && onResetWidth !== undefined ? (
              <ColumnResizeHandle
                columnKey={column.key}
                label={column.label}
                resized={resized.has(column.key)}
                onResizeStart={onResizeStart}
                onResize={onResize}
                onResetWidth={onResetWidth}
              />
            ) : null}
          </span>
        );
      })}

      {/* `Nudge` / `Reassign` live here on the delegation tab; elsewhere the column is 0 px. */}
      <span className="ez-tasks__colhead-cell ez-tasks__colhead-actions" aria-hidden="true" />
    </div>
  );
}

/**
 * The phone's one-line legend, under the tab strip (P-270).
 *
 * Always in the DOM and hidden above 767 px by CSS rather than rendered conditionally: the page
 * has no width state of its own, and a `usePhone()` branch here would make the sentence appear
 * one frame late on a rotation.
 */
export function TaskLegend({ tab }: { tab: TaskTab }) {
  return (
    <p className="ez-tasks__legend" data-testid="tasks-legend">
      {taskLegend(tab)}
    </p>
  );
}
